/**
 * sentry log view
 *
 * View detailed information about one or more Sentry log entries.
 */

import { isatty } from "node:tty";

import type { SentryContext } from "../../context.js";
import { getLogs } from "../../lib/api-client.js";
import {
  looksLikeIssueShortId,
  parseOrgProjectArg,
  parseSlashSeparatedArg,
} from "../../lib/arg-parsing.js";
import { openInBrowser } from "../../lib/browser.js";
import { buildCommand } from "../../lib/command.js";
import { ContextError, ValidationError } from "../../lib/errors.js";
import { formatLogDetails } from "../../lib/formatters/index.js";
import { filterFields } from "../../lib/formatters/json.js";
import { CommandOutput } from "../../lib/formatters/output.js";
import { ageInDaysFromUuidV7, validateHexId } from "../../lib/hex-id.js";
import {
  handleRecoveryResult,
  recoverHexId,
} from "../../lib/hex-id-recovery.js";
import {
  applyFreshFlag,
  FRESH_ALIASES,
  FRESH_FLAG,
} from "../../lib/list-command.js";
import { logger } from "../../lib/logger.js";
import {
  resolveOrgAndProject,
  resolveProjectBySlug,
} from "../../lib/resolve-target.js";
import { RETENTION_DAYS } from "../../lib/retention.js";
import { buildLogsUrl } from "../../lib/sentry-urls.js";
import { setOrgProjectContext } from "../../lib/telemetry.js";
import { isAllDigits } from "../../lib/utils.js";
import type { DetailedSentryLog } from "../../types/index.js";

const log = logger.withTag("log-view");

type ViewFlags = {
  readonly json: boolean;
  readonly web: boolean;
  readonly fresh: boolean;
  readonly fields?: string[];
};

/** Usage hint for ContextError messages */
const USAGE_HINT = "sentry log view <org>/<project> <log-id> [<log-id>...]";

/**
 * Split a raw argument into individual log IDs.
 * Handles newline-separated IDs within a single argument (common when
 * piping or pasting from other tools).
 *
 * @param arg - Raw positional argument
 * @returns Array of non-empty trimmed strings
 */
function splitLogIds(arg: string): string[] {
  return arg
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Parse positional arguments for log view.
 * Handles:
 * - `<log-id>` — single log ID (auto-detect org/project)
 * - `<target> <log-id> [<log-id>...]` — explicit target + one or more log IDs
 * - `<org>/<project>/<log-id>` — single slash-separated arg
 *
 * When two or more args are provided, the first is always treated as the
 * target (org/project specifier) and the rest as log IDs.
 *
 * Arguments containing newlines are split into multiple IDs.
 *
 * Returns **raw** log IDs without running {@link validateHexId} — validation
 * is deferred to the main command so {@link recoverHexId} can use the
 * resolved org/project context for fuzzy prefix lookups.
 *
 * @param args - Positional arguments from CLI
 * @returns Parsed raw log IDs and optional target arg
 * @throws {ContextError} If no arguments provided
 */
export function parsePositionalArgs(args: string[]): {
  rawLogIds: string[];
  targetArg: string | undefined;
  /** Suggestion when first arg looks like an issue short ID */
  suggestion?: string;
} {
  if (args.length === 0) {
    throw new ContextError("Log ID", USAGE_HINT, []);
  }

  const first = args[0];
  if (first === undefined) {
    throw new ContextError("Log ID", USAGE_HINT, []);
  }

  if (args.length === 1) {
    // Single arg — could be slash-separated org/project/logId or a plain ID
    // (possibly containing newlines)
    const { id, targetArg } = parseSlashSeparatedArg(
      first,
      "Log ID",
      USAGE_HINT
    );
    const rawLogIds = splitLogIds(id);
    if (rawLogIds.length === 0) {
      throw new ContextError("Log ID", USAGE_HINT, []);
    }
    return { rawLogIds, targetArg };
  }

  // Two or more args — first is target, rest are log IDs.
  // Each arg may contain newlines (split them).
  const rawLogIds = args.slice(1).flatMap(splitLogIds);
  if (rawLogIds.length === 0) {
    throw new ContextError("Log ID", USAGE_HINT, []);
  }
  // Swap detection is not useful here: log IDs cannot contain "/", so
  // detectSwappedViewArgs (which checks for "/" in the second arg) can
  // never trigger. We still check for issue short IDs in the first (target)
  // position.
  const suggestion = looksLikeIssueShortId(first)
    ? `Did you mean: sentry issue view ${first}`
    : undefined;

  return { rawLogIds, targetArg: first, suggestion };
}

/**
 * Validate and attempt to recover one log ID against the resolved target.
 *
 * For each raw ID: run {@link validateHexId}; on {@link ValidationError},
 * attempt {@link recoverHexId} with the resolved org/project. Successful
 * recovery returns a valid hex ID (and emits a `log.warn`).
 */
async function validateAndRecoverLogId(
  rawId: string,
  target: ResolvedLogTarget
): Promise<string> {
  try {
    return validateHexId(rawId, "log ID");
  } catch (err) {
    if (!(err instanceof ValidationError)) {
      throw err;
    }
    const result = await recoverHexId(rawId, "log", {
      org: target.org,
      project: target.project,
    });
    return handleRecoveryResult(result, err, {
      entityType: "log",
      canonicalCommand: `sentry log view ${target.org}/${target.project}/<id>`,
      logTag: "log.view",
    });
  }
}

/**
 * Resolved target type for log commands.
 * @internal Exported for testing
 */
export type ResolvedLogTarget = {
  org: string;
  project: string;
  detectedFrom?: string;
};

/**
 * Resolve the target org/project from the parsed arg.
 *
 * @param parsed - Result of `parseOrgProjectArg`
 * @param rawLogIds - Raw log IDs (used for usage hints; not yet validated)
 * @param cwd - Current working directory
 * @returns Resolved target, or null if resolution produced nothing
 * @throws {ContextError} If org-all mode is used (requires specific project)
 */
async function resolveTarget(
  parsed: ReturnType<typeof parseOrgProjectArg>,
  rawLogIds: string[],
  cwd: string
): Promise<ResolvedLogTarget | null> {
  switch (parsed.type) {
    case "explicit":
      setOrgProjectContext([parsed.org], [parsed.project]);
      return { org: parsed.org, project: parsed.project };

    case "project-search": {
      const result = await resolveProjectBySlug(
        parsed.projectSlug,
        USAGE_HINT,
        `sentry log view <org>/${parsed.projectSlug} ${rawLogIds.join(" ")}`,
        parsed.originalSlug
      );
      if (
        isAllDigits(parsed.projectSlug) &&
        result.project !== parsed.projectSlug
      ) {
        log.info(
          `Tip: Resolved project ID ${parsed.projectSlug} to ${result.org}/${result.project}. ` +
            "Use the slug form for faster lookups."
        );
      }
      return result;
    }

    case "org-all":
      throw new ContextError("Specific project", USAGE_HINT, []);

    case "auto-detect":
      return resolveOrgAndProject({ cwd, usageHint: USAGE_HINT });

    default: {
      const _exhaustiveCheck: never = parsed;
      throw new ValidationError(
        `Invalid target specification: ${_exhaustiveCheck}`
      );
    }
  }
}

/**
 * Format a list of log IDs as a markdown bullet list.
 *
 * @param ids - Log IDs to format
 * @returns Markdown list string with each ID on its own line
 */
function formatIdList(ids: string[]): string {
  return ids.map((id) => ` - \`${id}\``).join("\n");
}

/**
 * Warn about IDs that weren't found in the API response.
 * Uses the consola logger for structured output to stderr.
 *
 * @param logIds - All requested IDs
 * @param logs - Logs actually returned by the API
 */
function warnMissingIds(logIds: string[], logs: DetailedSentryLog[]): void {
  if (logs.length >= logIds.length) {
    return;
  }
  const foundIds = new Set(logs.map((l) => l["sentry.item_id"]));
  const missing = logIds.filter((id) => !foundIds.has(id));
  if (missing.length > 0) {
    log.warn(
      `${missing.length} of ${logIds.length} log(s) not found:\n${formatIdList(missing)}`
    );
  }
}

/**
 * Handle --web flag: open log URLs in the browser.
 * Prompts for confirmation in interactive mode when multiple IDs are given.
 * Aborts in non-interactive mode with a warning.
 *
 * @param orgSlug - Organization slug for URL building
 * @param logIds - Log IDs to open
 */
async function handleWebOpen(orgSlug: string, logIds: string[]): Promise<void> {
  if (logIds.length > 1) {
    if (!isatty(0)) {
      log.warn(
        `Refusing to open ${logIds.length} browser tabs in non-interactive mode. ` +
          "Pass a single log ID or run interactively."
      );
      return;
    }
    const confirmed = await log.prompt(`Open ${logIds.length} browser tabs?`, {
      type: "confirm",
      initial: false,
    });
    // consola prompt returns Symbol(clack:cancel) on Ctrl+C — a truthy value.
    // Strictly check for `true` to avoid opening tabs on cancel.
    if (confirmed !== true) {
      return;
    }
  }
  for (const id of logIds) {
    await openInBrowser(buildLogsUrl(orgSlug, id), "log");
  }
}

/**
 * Build a retention-aware message for logs the API couldn't find.
 *
 * When a log ID is UUIDv7 (as Sentry emits) and its embedded timestamp is
 * older than the hard retention cap, we can state with certainty that
 * it's past retention rather than hedging with "may have been deleted".
 *
 * Returns a per-ID annotation like " (created 2025-12-15, past 90-day
 * retention)" when applicable, else an empty string.
 */
function retentionSuffix(logId: string): string {
  const retention = RETENTION_DAYS.log;
  if (retention === null) {
    return "";
  }
  const age = ageInDaysFromUuidV7(logId);
  if (age === null || age <= retention) {
    return "";
  }
  const days = Math.floor(age);
  return ` (created ${days} days ago, past the ${retention}-day log retention)`;
}

/**
 * Throw a descriptive error when no logs were found.
 *
 * @param logIds - Requested IDs
 * @param org - Organization slug
 * @param project - Project slug
 * @throws {ValidationError} Always
 */
function throwNotFoundError(
  logIds: string[],
  org: string,
  project: string
): never {
  // Generic fallback wording references `RETENTION_DAYS.log` so a single
  // edit in `retention.ts` keeps this message in sync with the
  // deterministic retention-aware path.
  const retentionDays = RETENTION_DAYS.log;
  const genericHint = retentionDays
    ? `Make sure the log IDs are correct and were sent within the last ${retentionDays} days.`
    : "Make sure the log IDs are correct.";

  if (logIds.length === 1) {
    const id = logIds[0] ?? "";
    const suffix = retentionSuffix(id);
    const hint = suffix
      ? `This log is no longer retrievable.${suffix}`
      : genericHint.replace("log IDs are correct", "log ID is correct");
    throw new ValidationError(
      `No log found with ID "${id}" in ${org}/${project}.\n\n${hint}`
    );
  }

  // Multiple IDs — compute the retention suffix once per ID so both the
  // inline annotation and the "any expired?" check reuse the same decode.
  const suffixed = logIds.map((id) => ({ id, suffix: retentionSuffix(id) }));
  const annotated = suffixed
    .map(({ id, suffix }) => ` - \`${id}\`${suffix}`)
    .join("\n");
  const anyExpired = suffixed.some(({ suffix }) => suffix !== "");
  const hint = anyExpired
    ? "Expired log IDs are no longer retrievable. Check non-expired IDs and re-run."
    : genericHint;
  throw new ValidationError(
    `No logs found with any of the following IDs in ${org}/${project}:\n${annotated}\n\n${hint}`
  );
}

/**
 * Data returned by the log view command.
 * Used by both JSON and human output paths.
 */
type LogViewData = {
  /** Retrieved log entries */
  logs: DetailedSentryLog[];
  /** Org slug — needed by human formatter for trace URLs, also useful context in JSON */
  orgSlug: string;
};

/**
 * Format log view data as human-readable output.
 *
 * Each log entry is formatted with full details. Multiple entries
 * are separated by horizontal rules.
 *
 * @param data - Log view data with entries and org slug
 * @returns Formatted string for terminal output
 */
function formatLogViewHuman(data: LogViewData): string {
  const parts: string[] = [];
  for (const entry of data.logs) {
    if (parts.length > 0) {
      parts.push("\n---\n");
    }
    parts.push(formatLogDetails(entry, data.orgSlug));
  }
  return parts.join("\n");
}

export const viewCommand = buildCommand({
  docs: {
    brief: "View details of one or more log entries",
    fullDescription:
      "View detailed information about Sentry log entries by their IDs.\n\n" +
      "Target specification:\n" +
      "  sentry log view <log-id>                          # auto-detect from DSN or config\n" +
      "  sentry log view <org>/<proj> <log-id> [<id>...]   # explicit org and project\n" +
      "  sentry log view <project> <log-id> [<id>...]      # find project across all orgs\n\n" +
      "Multiple log IDs can be passed as separate arguments or newline-separated\n" +
      "within a single argument (handy when piping from other commands).\n\n" +
      "The log ID is the 32-character hexadecimal identifier shown in log listings.",
  },
  output: {
    human: formatLogViewHuman,
    // Preserve original JSON contract: bare array of log entries.
    // orgSlug exists only for the human formatter (trace URLs).
    jsonTransform: (data: LogViewData, fields) =>
      fields && fields.length > 0
        ? data.logs.map((entry) => filterFields(entry, fields))
        : data.logs,
  },
  parameters: {
    positional: {
      kind: "array",
      parameter: {
        placeholder: "org/project/log-id",
        brief:
          "[<org>/<project>] <log-id> [<log-id>...] - Target (optional) and one or more log IDs",
        parse: String,
      },
    },
    flags: {
      web: {
        kind: "boolean",
        brief: "Open in browser",
        default: false,
      },
      fresh: FRESH_FLAG,
    },
    aliases: { ...FRESH_ALIASES, w: "web" },
  },
  async *func(this: SentryContext, flags: ViewFlags, ...args: string[]) {
    applyFreshFlag(flags);
    const { cwd } = this;
    const cmdLog = logger.withTag("log.view");

    // Parse positional args (raw — validation is deferred to after target
    // resolution so we can run fuzzy recovery with org/project context).
    const { rawLogIds, targetArg, suggestion } = parsePositionalArgs(args);
    if (suggestion) {
      cmdLog.warn(suggestion);
    }
    const parsed = parseOrgProjectArg(targetArg);

    const target = await resolveTarget(parsed, rawLogIds, cwd);

    if (!target) {
      throw new ContextError("Organization and project", USAGE_HINT);
    }

    // Validate + recover each log ID in parallel. `log.warn` preserves
    // emission order regardless of await order, and a throwing recovery
    // (multi-match ResolutionError) exits via `Promise.all` with the
    // first-thrown error — same UX as the old sequential loop.
    const logIds = await Promise.all(
      rawLogIds.map((raw) => validateAndRecoverLogId(raw, target))
    );

    if (flags.web) {
      await handleWebOpen(target.org, logIds);
      return;
    }

    // Fetch all requested log entries
    const logs = await getLogs(target.org, target.project, logIds);

    if (logs.length === 0) {
      throwNotFoundError(logIds, target.org, target.project);
    }

    warnMissingIds(logIds, logs);

    const hint = target.detectedFrom
      ? `Detected from ${target.detectedFrom}`
      : undefined;

    yield new CommandOutput({ logs, orgSlug: target.org });
    return { hint };
  },
});
